import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useDateRange } from "@/context/DateRangeContext";
import { fmtMoney, fmtNum, fmtMonth } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { EmptyState, Loading, ChartTooltip } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { DollarSign, ShoppingCart, Package, TrendingUp, Globe, Upload, Download } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import ProductThumb from "@/components/ProductThumb";

export default function OverviewTab({ onUpload }) {
  const { currency } = useCurrency();
  const { start, end } = useDateRange();
  const [loading, setLoading] = useState(true);
  const [ov, setOv] = useState(null);
  const [trend, setTrend] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [regional, setRegional] = useState({ countries: [], channels: [] });
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    const dr = `&start=${start}&end=${end}`;
    Promise.all([
      api.get(`/analytics/overview?currency=${currency}${dr}`),
      api.get(`/analytics/sales-trend?currency=${currency}&granularity=day${dr}`),
      api.get(`/analytics/sales-trend?currency=${currency}&granularity=month${dr}`),
      api.get(`/analytics/regional?currency=${currency}${dr}`),
      api.get(`/analytics/products?currency=${currency}${dr}`),
    ]).then(([o, t, m, r, p]) => {
      if (!ok) return;
      setOv(o.data);
      setTrend(t.data.data);
      setMonthly(m.data.data);
      setRegional(r.data);
      setProducts(p.data.data);
      setLoading(false);
    });
    return () => { ok = false; };
  }, [currency, start, end]);

  if (loading) return <Loading />;
  if (!ov || ov.orders === 0) {
    return (
      <EmptyState
        title="No sales data yet"
        message="Upload an Amazon 'All Orders' report or load the sample dataset to see your sales analytics come to life."
        action={
          <Button onClick={onUpload} data-testid="empty-upload-cta" className="mt-6 bg-[#007AFF] hover:bg-[#005BB5] rounded-full px-6">
            <Upload className="h-4 w-4 mr-2" /> Import data
          </Button>
        }
      />
    );
  }

  const BAR_COLORS = ["#007AFF", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-revenue" label="Total Revenue" value={fmtMoney(ov.revenue, currency, true)} sub="gross sales" icon={DollarSign} accent="#007AFF" />
        <StatCard testid="kpi-profit" label="Net Profit (est.)" value={fmtMoney(ov.net_profit, currency, true)} sub={`${ov.margin}% margin`} icon={TrendingUp} accent="#10B981" />
        <StatCard testid="kpi-orders" label="Orders" value={fmtNum(ov.orders)} sub={`AOV ${fmtMoney(ov.avg_order_value, currency)}`} icon={ShoppingCart} accent="#F59E0B" />
        <StatCard testid="kpi-units" label="Units Sold" value={fmtNum(ov.units)} sub="total items" icon={Package} accent="#8B5CF6" />
      </div>

      {/* Sales trend */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="sales-trend-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-head font-bold text-white">Sales Trend</h3>
            <p className="text-xs text-[#666]">Daily revenue over the last period</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={trend} margin={{ left: -10, right: 8, top: 8 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
            <XAxis dataKey="period" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} minTickGap={40} />
            <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtMoney(v, currency, true)} />
            <Tooltip content={<ChartTooltip currency={currency} moneyKeys={["revenue"]} />} />
            <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#007AFF" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly sales */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="monthly-sales-chart">
          <h3 className="font-head font-bold text-white mb-1">Monthly Sales</h3>
          <p className="text-xs text-[#666] mb-4">Revenue grouped by month</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly.map((m) => ({ ...m, period: fmtMonth(m.period) }))} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} />
              <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtMoney(v, currency, true)} />
              <Tooltip cursor={{ fill: "#ffffff08" }} content={<ChartTooltip currency={currency} moneyKeys={["revenue"]} />} />
              <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]} fill="#007AFF" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Regional */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="regional-sales-chart">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="h-4 w-4 text-[#10B981]" />
            <h3 className="font-head font-bold text-white">Regional Sales</h3>
          </div>
          <p className="text-xs text-[#666] mb-4">Revenue by destination country</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={regional.countries} layout="vertical" margin={{ left: 10, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtMoney(v, currency, true)} />
              <YAxis type="category" dataKey="country_name" tick={{ fill: "#A3A3A3", fontSize: 11 }} tickLine={false} axisLine={false} width={90} />
              <Tooltip cursor={{ fill: "#ffffff08" }} content={<ChartTooltip currency={currency} moneyKeys={["revenue"]} />} />
              <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                {regional.countries.map((_, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Product performance */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="product-performance-table">
        <div className="p-5 border-b border-[#1a1a1a] flex items-center justify-between">
          <div>
            <h3 className="font-head font-bold text-white">Product Performance</h3>
            <p className="text-xs text-[#666]">Top products by revenue</p>
          </div>
          <Button
            data-testid="export-products-csv"
            onClick={() => downloadCSV(`products-${currency}.csv`, products)}
            variant="outline"
            className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">Product</th>
                <th className="px-5 py-3 font-semibold">ASIN / SKU</th>
                <th className="px-5 py-3 font-semibold text-right">Units</th>
                <th className="px-5 py-3 font-semibold text-right">Revenue</th>
                <th className="px-5 py-3 font-semibold text-right">Net Profit</th>
                <th className="px-5 py-3 font-semibold text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {products.slice(0, 12).map((p) => (
                <tr key={p.sku} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5 max-w-[260px]">
                      <ProductThumb src={p.image} size={32} title={p.product_name} />
                      <span className="font-medium text-white truncate" title={p.product_name}>{p.product_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{p.asin || "—"}<br /><span className="text-[#555]">{p.sku}</span></td>
                  <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(p.units)}</td>
                  <td className="px-5 py-3 text-right tnum text-white font-semibold">{fmtMoney(p.revenue, currency, true)}</td>
                  <td className={`px-5 py-3 text-right tnum font-semibold ${p.net_profit >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{fmtMoney(p.net_profit, currency, true)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{p.margin}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#555]">Net profit & margin are estimated (15% referral + $3/unit FBA fee + COGS). Upload settlement reports or set unit costs in Inventory for accuracy.</p>
    </div>
  );
}
